import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useFormik, FormikProvider } from 'formik'
import * as Yup from 'yup'
import {
  alpha,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material'
import { Close, TrendingDown, TrendingUp } from '@mui/icons-material'

import { DialogsEnum } from '@/enums'
import useDialog from '@/hooks/ui/use-dialog.hook'
import { colors } from '@/theme/colors.theme'
import { ApplicationState } from '@/state/root.models'
import { usePlayerCardActions } from '@/views/player-card/hooks'
import InputFormikField from '@/views/components/input-formik-field/index.component'

export const DialogManualDebitCreditForm = () => {
  const { t } = useTranslation()
  const { dialog, handleCloseDialog } = useDialog()
  const { setPlayerBalance } = usePlayerCardActions()
  const [type, setType] = useState<'credit' | 'debit'>('credit')

  const { wallet: playerCardState, playerCard } = useSelector(
    ({ playerCard }: ApplicationState) => playerCard,
  )

  const currentBalance = playerCardState?.balance || 0
  const currencySymbol = playerCardState?.currency || '$'

  const validationSchema = Yup.object().shape({
    amount: Yup.number()
      .typeError(t('messages.formatError', { field: t('labels.amount') }))
      .positive(t('messages.formatError', { field: t('labels.amount') }))
      .max(999999999, t('messages.formatError', { field: t('labels.amount') }))
      .test(
        'check-balance',
        t('messages.insufficientFunds', 'Not enough balance to remove'),
        (value) => {
          if (type === 'debit' && value !== undefined) {
            return value <= currentBalance
          }
          return true
        },
      )
      .required(t('messages.incompleteFields')),
    description: Yup.string().trim().required(t('messages.incompleteFields')),
  })

  const formik = useFormik({
    initialValues: {
      amount: '',
      description: '',
    },
    validationSchema,
    onSubmit: (values) => {
      const enteredAmount = Number(values.amount)
      const finalAmount = type === 'credit' ? enteredAmount : -enteredAmount

      setPlayerBalance({
        playerId: playerCard?.id || playerCardState?.playerId || '',
        description: values.description,
        amount: finalAmount,
      })
      handleCloseDialog()
    },
    enableReinitialize: true,
  })

  useEffect(() => {
    if (dialog.open && dialog.id === DialogsEnum.DIALOG_MANUAL_DEBIT_CREDIT) {
      formik.resetForm()
      setType('credit')
    }
  }, [dialog.open, dialog.id]) // eslint-disable-line

  useEffect(() => {
    formik.validateForm()
  }, [type]) // eslint-disable-line

  const enteredAmount = Number(formik.values.amount) || 0
  const totalBalance =
    type === 'credit'
      ? currentBalance + enteredAmount
      : currentBalance - enteredAmount

  const formatCurrency = (val: number) => {
    return `${currencySymbol} ${Number(val).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`
  }

  const isOpen =
    dialog.open && dialog.id === DialogsEnum.DIALOG_MANUAL_DEBIT_CREDIT

  return (
    <Dialog
      open={isOpen}
      onClose={handleCloseDialog}
      aria-labelledby="dialog-title"
      fullWidth
      sx={{
        '& .MuiDialog-paperFullWidth': {
          width: '600px',
          maxWidth: '100%',
          backgroundColor: '#1E2630',
          color: colors.white,
          borderRadius: '12px',
        },
      }}
    >
      <DialogTitle id="dialog-title" sx={{ m: 0, p: 2, pb: 1 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
        >
          <Typography
            fontWeight={700}
            variant="h6"
            sx={{ fontSize: '1.25rem' }}
          >
            {t('playerCard.manualDebitCredit', 'Manual Debit/Credit')}
          </Typography>
          <IconButton
            onClick={handleCloseDialog}
            sx={{ color: alpha(colors.white, 0.7) }}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 2, pt: 1 }}>
        <FormikProvider value={formik}>
          <Grid container spacing={2}>
            {/* Toggle buttons for Credit & Debit */}
            <Grid item xs={12}>
              <Box display="flex" gap={2} mb={1}>
                {/* Credit Option */}
                <Box
                  onClick={() => setType('credit')}
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px 10px',
                    borderRadius: '8px',
                    border: `1px solid ${
                      type === 'credit'
                        ? colors.white
                        : alpha(colors.white, 0.1)
                    }`,
                    backgroundColor:
                      type === 'credit'
                        ? alpha(colors.white, 0.03)
                        : 'transparent',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: alpha(colors.white, 0.05),
                      borderColor:
                        type === 'credit'
                          ? colors.white
                          : alpha(colors.white, 0.2),
                    },
                  }}
                >
                  <TrendingUp
                    sx={{ fontSize: 24, mb: 1, color: colors.white }}
                  />
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: colors.white }}
                  >
                    {t('playerCard.credit', 'Credit')}
                  </Typography>
                </Box>

                {/* Debit Option */}
                <Box
                  onClick={() => setType('debit')}
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px 10px',
                    borderRadius: '8px',
                    border: `1px solid ${
                      type === 'debit' ? colors.white : alpha(colors.white, 0.1)
                    }`,
                    backgroundColor:
                      type === 'debit'
                        ? alpha(colors.white, 0.03)
                        : 'transparent',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: alpha(colors.white, 0.05),
                      borderColor:
                        type === 'debit'
                          ? colors.white
                          : alpha(colors.white, 0.2),
                    },
                  }}
                >
                  <TrendingDown
                    sx={{ fontSize: 24, mb: 1, color: colors.white }}
                  />
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: colors.white }}
                  >
                    {t('playerCard.debit', 'Debit')}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Amount input */}
            <Grid item xs={12}>
              <InputFormikField
                name="amount"
                type="number"
                placeholder={t('labels.amount')}
                onKeyDown={(e) => {
                  if (['e', 'E', '+', '-'].includes(e.key)) {
                    e.preventDefault()
                  }
                }}
                onChange={(e) => {
                  const val = e.target.value
                  const cleanVal = val.replace(/[eE+-]/g, '').replace(/,/g, '.')
                  const parts = cleanVal.split('.')

                  let integerPart = parts[0]
                  if (integerPart.length > 9) {
                    integerPart = integerPart.slice(0, 9)
                  }

                  let decimalPart = parts[1]
                  if (decimalPart !== undefined) {
                    decimalPart = decimalPart.slice(0, 2)
                  }

                  e.target.value =
                    decimalPart !== undefined
                      ? `${integerPart}.${decimalPart}`
                      : integerPart

                  formik.handleChange(e)
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography sx={{ color: alpha(colors.white, 0.4) }}>
                        {currencySymbol}
                      </Typography>
                    </InputAdornment>
                  ),
                  sx: {
                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                    borderColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              />
            </Grid>

            {/* Comment input */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="description"
                label={t('labels.comment')}
                multiline
                rows={3}
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.description &&
                  Boolean(formik.errors.description)
                }
                helperText={
                  formik.touched.description && formik.errors.description
                }
                InputProps={{
                  sx: {
                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                    borderColor: 'rgba(255, 255, 255, 0.1)',
                    '& textarea': {
                      height: 'auto !important',
                    },
                  },
                }}
              />
            </Grid>

            {/* Details section */}
            <Grid item xs={12}>
              <Box
                sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}
              >
                <Box display="flex" justifyContent="space-between">
                  <Typography
                    variant="body2"
                    sx={{ color: alpha(colors.white, 0.7) }}
                  >
                    {t('playerCard.currentBalance', 'Current Balance:')}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatCurrency(currentBalance)}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography
                    variant="body2"
                    sx={{ color: alpha(colors.white, 0.7) }}
                  >
                    {type === 'credit'
                      ? t('playerCard.amountToBeAdded', 'Amount to be added:')
                      : t(
                          'playerCard.amountToBeRemoved',
                          'Amount to be removed:',
                        )}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatCurrency(enteredAmount)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    borderBottom: '1px dashed rgba(255, 255, 255, 0.15)',
                    my: 1,
                    width: '100%',
                  }}
                />
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" fontWeight={700}>
                    {t('playerCard.totalTokens', 'Total Tokens:')}
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {formatCurrency(totalBalance)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </FormikProvider>
        <DialogActions sx={{ padding: '10px 0px' }}>
          <Button onClick={handleCloseDialog} className="red">
            {t('labels.cancel')}
          </Button>
          <Button
            disabled={!formik.isValid || !formik.dirty}
            variant="contained"
            onClick={() => formik.handleSubmit()}
          >
            {type === 'credit'
              ? t('playerCard.applyCredit', 'Apply Credit')
              : t('playerCard.applyDebit', 'Apply Debit')}
          </Button>
        </DialogActions>
      </DialogContent>
    </Dialog>
  )
}
